/**
 * Dataspace Context Store (Orgs v1)
 * 
 * Manages the user's active dataspace context (Personal vs Org).
 * Handles switching between dataspaces and loading user's organizations.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createFileLogger } from '../utils/logger';
import fetchService from '../services/fetchService';
import { API_BASE_URL } from '../api/config';
import useUserStore from './userStore';

const log = createFileLogger('DATASPACE_CONTEXT_STORE', {enabled: false});

// Types
export interface DataspaceContext {
  type: 'org';
  orgId: string | null;
  orgName: string | null;
}

export interface PendingNavigation {
  type: 'space' | 'home';
  spaceId?: string;
}

export interface Org {
  _id: string;
  id: string;
  entityType: 'Org';
  name: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  skeleton: {
    '@type'?: string;
    displayName: string;
    slug: string;
    description?: string;
    logo_url?: string;
    owner_user_id: string;
    member_user_ids: string[];
    viewer_user_ids: string[];
    orggroup_ids: string[];
    root_group_ids: string[];
    billing_plan: string;
    billing_status?: string;
    billing_schema_version?: string;
    billing_subscription_id?: string;
    cancel_at_period_end?: boolean;
    cancel_at?: string;
    is_personal?: boolean;
    seat_count?: number;
    shared_credit_balance: number;
    monthly_credit_refresh: number;
  };
  acl: any[];
  // The requesting user's individual billing plan within this org.
  // Populated by GET /api/orgs from org_member_billing; distinct from
  // skeleton.billing_plan which reflects the org owner's plan.
  my_plan?: string;
}

export interface PaymentGateState {
  isOpen: boolean;
  orgId: string | null;
  orgName: string | null;
  seatCount: number;
  planTier?: string;  // ✅ FIX #6: Track selected plan tier
}

/**
 * A personal workspace is one the user owns alone with no other members of any role.
 * Prefers the explicit `is_personal` flag set by the backend at creation time.
 * Falls back to the member-count heuristic for legacy orgs without the flag.
 */
export function isPersonalOrg(org: Org, currentUserId?: string): boolean {
  const skel = org.skeleton;
  if (skel.is_personal === true) return true;
  if (skel.is_personal === false) return false;
  const isOwner = currentUserId ? skel.owner_user_id === currentUserId : true;
  return isOwner &&
    (skel.member_user_ids?.length ?? 0) === 0 &&
    (skel.viewer_user_ids?.length ?? 0) === 0;
}

/**
 * Find the current user's personal workspace org from a list of orgs.
 */
export function findPersonalOrg(orgs: Org[], currentUserId: string): Org | undefined {
  return orgs.find(org => isPersonalOrg(org, currentUserId));
}

/**
 * Returns true if the user is the owner of the org.
 */
export function isOrgAdmin(org: Org, userId: string): boolean {
  return org.skeleton.owner_user_id === userId;
}

interface DataspaceContextState {
  // State
  currentContext: DataspaceContext;
  userOrgs: Org[];
  isLoading: boolean;
  error: string | null;
  pendingNavigation: PendingNavigation | null;
  lastVisitedSpaces: Record<string, string>; // Maps dataspace key (personal or orgId) to spaceId
  paymentGate: PaymentGateState;
  lastOrgLoadAt: number | null; // Timestamp (ms) of last successful loadUserOrgs call
  rootSpaceId: string | null; // Root Space for the current org (pre-warmed after context load)

  // Actions
  setContext: (context: DataspaceContext) => Promise<void>;
  loadUserOrgs: () => Promise<void>;
  switchToPersonal: () => Promise<void>;
  switchToOrg: (orgId: string) => Promise<void>;
  refreshContext: () => Promise<void>;
  applyRemoteContextChange: (orgId: string, orgName: string | null) => Promise<void>;
  clearError: () => void;
  setPendingNavigation: (nav: PendingNavigation | null) => void;
  setLastVisitedSpace: (spaceId: string) => void;
  getLastVisitedSpace: (contextKey?: string) => string | null;
  showPaymentGate: (orgId: string) => void;
  hidePaymentGate: () => void;
      updatePaymentGatePlan: (planTier: string) => void;  // ✅ FIX #6: Update plan selection
}

/**
 * Dataspace Context Store
 * 
 * Manages switching between Personal and Org dataspaces.
 * Persists the current context to localStorage and backend.
 */
export const useDataspaceContextStore = create<DataspaceContextState>()(
  persist(
    (set, get) => ({
      // Initial state — orgId is null until synced from backend via GET /user/context.
      // type is always 'org'; every user has a personal org created at signup.
      currentContext: {
        type: 'org',
        orgId: null,
        orgName: null,
      },
      userOrgs: [],
      isLoading: false,
      error: null,
      pendingNavigation: null,
      lastVisitedSpaces: {},
      lastOrgLoadAt: null,
      rootSpaceId: null,
      paymentGate: {
        isOpen: false,
        orgId: null,
        orgName: null,
        seatCount: 1
      },
      
      // Set dataspace context
      setContext: async (context: DataspaceContext) => {
        set({ isLoading: true, error: null });
        
        try {
          log.info('[setContext] Switching to', context);
          
          // Flush all pending saves BEFORE switching dataspace to prevent data loss.
          // Uses a simple retry loop: transient network errors are retried up to 2 times.
          // If all retries fail we log a warning and continue — blocking the switch
          // permanently on a save failure would leave the user stuck.
          const MAX_FLUSH_RETRIES = 2;
          const FLUSH_RETRY_DELAY_MS = 600;
          let flushAttempt = 0;
          let flushSucceeded = false;
          while (flushAttempt <= MAX_FLUSH_RETRIES && !flushSucceeded) {
            if (flushAttempt > 0) {
              log.warn(`[setContext] Retrying saves flush (attempt ${flushAttempt}/${MAX_FLUSH_RETRIES})...`);
              await new Promise(r => setTimeout(r, FLUSH_RETRY_DELAY_MS));
            }
            try {
              log.info('[setContext] 🔄 Flushing all pending saves before dataspace switch...');

              const { docsCRDTService } = await import('../apps/DocsApp/services/crdt/docsCRDTService');
              await docsCRDTService.flushAllPendingSaves();
              log.info('[setContext] ✅ DocsApp saves flushed');

              const { notesCRDTService } = await import('../apps/NotesMarkdownApp/services/notesCRDTService');
              await notesCRDTService.flushAllPendingSaves();
              log.info('[setContext] ✅ NotesApp saves flushed');

              log.info('[setContext] ✅ All saves flushed successfully');
              flushSucceeded = true;
            } catch (flushError) {
              flushAttempt++;
              log.error(`[setContext] ⚠️ Error flushing saves (attempt ${flushAttempt}):`, flushError);
            }
          }
          if (!flushSucceeded) {
            log.error('[setContext] ❌ All flush retries failed — proceeding with context switch. Any unsaved changes in open documents may be lost.');
          }
          
          // Persist context to backend
          const data = await fetchService(`${API_BASE_URL}/user/context`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              context_type: context.type,
              org_id: context.orgId
            })
          });
          
          const newContext = {
            type: data.type,
            orgId: data.org_id || null,
            orgName: data.org_name || null
          };

          // Write this store first, then mirror into systemStore atomically (finding #7).
          set({ currentContext: newContext, rootSpaceId: null });
          try {
            const { useSystemStore } = await import('./systemStore');
            useSystemStore.setState({ currentContext: newContext, recentSpaces: [], spacesCacheTime: null });
          } catch {
            // non-fatal — context divergence canary will flag it in dev
          }

          // Pre-warm the Root Space ID for the new org so quick-creates from Home are instant.
          if (newContext.orgId) {
            const orgIdForPrewarm = newContext.orgId;
            import('../services/rootSpaceService').then(({ getOrCreateRootSpaceId }) => {
              getOrCreateRootSpaceId(orgIdForPrewarm)
                .then(id => {
                  if (get().currentContext.orgId === orgIdForPrewarm) {
                    set({ rootSpaceId: id });
                    log.info(`[setContext] root space pre-warmed: ${id.slice(-8)} for org ${orgIdForPrewarm.slice(-8)}`);
                  }
                })
                .catch(e => log.warn('[setContext] root space pre-warm failed (non-fatal):', e));
            }).catch(() => {});
          }

          // ✅ ORGS V1: Switch to dataspace-specific entity cache
          try {
            const { default: useEntityStore } = await import('./entityStore');
            const entityStore = useEntityStore.getState();
            
            const dataspaceKey = data.org_id || 'unknown';

            // Persist current entities to their dataspace cache via setState (finding #13:
            // direct property mutation bypasses Zustand subscribers).
            if (entityStore.currentDataspaceKey && entityStore.entities.length > 0) {
              const prevKey = entityStore.currentDataspaceKey;
              useEntityStore.setState(s => ({
                dataspaceEntities: {
                  ...s.dataspaceEntities,
                  [prevKey]: [...s.entities],
                },
              }));
              log.info(`[setContext] 💾 Saved ${entityStore.entities.length} entities for ${prevKey}`);
            }

            // Switch to new dataspace
            useEntityStore.setState({ currentDataspaceKey: dataspaceKey });
            log.info(`[setContext] ✅ Updated currentDataspaceKey to: ${dataspaceKey}`);

            // Clear IndexedDB cache for this dataspace to force fresh fetch
            try {
              const { entityCacheService } = await import('../services/entityCacheService');
              const { getCurrentUserId } = await import('../utils/userIdUtils');
              const { getDataspaceCacheKey } = await import('../utils/entityCacheUtils');
              const userId = getCurrentUserId();

              if (userId) {
                const cacheKeyToDelete = getDataspaceCacheKey(userId, data.type, data.org_id);
                await entityCacheService.delete(cacheKeyToDelete);
                log.info(`[setContext] 🗑️ Cleared stale cache for dataspace: ${cacheKeyToDelete.substring(0, 40)}...`);
              }
            } catch (cacheError) {
              log.warn(`[setContext] Failed to clear cache:`, cacheError);
            }

            // Clear the in-memory cache for this dataspace via setState and read the
            // (now-absent) entry to decide whether to fast-path from cache or re-fetch.
            // (Finding #22: deleting then immediately reading the same key was always
            // undefined — the cached-entities branch was dead code. Keep the delete but
            // remove the impossible cache hit.)
            useEntityStore.setState(s => {
              const next = { ...s.dataspaceEntities };
              delete next[dataspaceKey];
              return { dataspaceEntities: next };
            });

            // Trigger fresh entity fetch for the new dataspace.
            useEntityStore.setState({
              entities: [],
              initialized: false,
              status: 'idle',
              lifecycle: 'uninitialized',
              initializationPromise: null,
            });
            log.info(`[setContext] 🔄 Triggering entity reload for dataspace: ${dataspaceKey}`);

            const { getCurrentUserId } = await import('../utils/userIdUtils');
            const userId = getCurrentUserId();

            const dataspaceRoomKey = data.type === 'org' && data.org_id
              ? `org:${data.org_id}`
              : `user:${userId}`;

            try {
              log.info(`[setContext] Re-initializing entityStore with dataspaceRoomKey: ${dataspaceRoomKey}`);
              await useEntityStore.getState().initialize(dataspaceRoomKey);
              log.info(`[setContext] ✅ EntityStore re-initialized for dataspace: ${dataspaceKey}`);
            } catch (fetchError) {
              log.error(`[setContext] Failed to re-initialize entityStore for ${dataspaceKey}:`, fetchError);
            }
            
          } catch (error) {
            log.error('[setContext] Failed to switch entityStore dataspace:', error);
          }

          // Re-fetch user profile and billing so plan/usage reflects the new org.
          // Non-blocking: failures are logged but don't prevent the switch.
          try {
            const userStore = (await import('./userStore')).default;
            userStore.getState().fetchProfile().catch((e: any) =>
              log.warn('[setContext] Non-fatal: fetchProfile failed after switch', e)
            );
            userStore.getState().fetchCurrentBill({ force: true }).catch((e: any) =>
              log.warn('[setContext] Non-fatal: fetchCurrentBill failed after switch', e)
            );
          } catch { /* dynamic import failed — non-fatal */ }

          log.info(`[setContext] Switched to ${context.type} dataspace`);
          
        } catch (error: any) {
          log.error('[setContext] Error:', error);
          set({ error: error.message || 'Failed to switch dataspace' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      
      // Load user's organizations
      loadUserOrgs: async () => {
        set({ isLoading: true, error: null });
        
        try {
          log.info('[loadUserOrgs] Loading user orgs');
          
          const orgs: Org[] = await fetchService(`${API_BASE_URL}/orgs`, {
            method: 'GET'
          });
          
          // Check for new org memberships and show notifications
          const { checkForNewOrgs, showNewOrgNotification, markOrgsAsSeen } = await import('../utils/orgNotifications');
          const currentOrgIds = orgs.map(org => org._id || org.id);
          const newOrgIds = checkForNewOrgs(currentOrgIds);
          
          // Show notifications for new orgs (excluding personal orgs)
          if (newOrgIds.length > 0) {
            log.info(`[loadUserOrgs] Found ${newOrgIds.length} new org(s), showing notifications`);
            const currentUserId = useUserStore.getState().auth0Id ?? undefined;
            newOrgIds.forEach((newOrgId: string) => {
              const org = orgs.find(o => (o._id || o.id) === newOrgId);
              if (org && !isPersonalOrg(org, currentUserId)) {
                const orgName = org.skeleton.displayName || org.name;
                showNewOrgNotification(newOrgId, orgName, (orgId: string) => {
                  // Switch to the org when button is clicked
                  get().switchToOrg(orgId);
                });
              }
            });
            
            // Mark all orgs as seen
            markOrgsAsSeen(currentOrgIds);
          }
          
          set({ userOrgs: orgs, lastOrgLoadAt: Date.now() });

          log.info(`[loadUserOrgs] Loaded ${orgs.length} orgs`);
          // Root Space pre-warm is handled by setContext, which runs before or
          // concurrently with loadUserOrgs. No duplicate pre-warm needed here.

        } catch (error: any) {
          log.error('[loadUserOrgs] Error:', error);
          set({ error: error.message || 'Failed to load organizations' });
        } finally {
          set({ isLoading: false });
        }
      },
      
      // Switch to personal dataspace (the user's personal org)
      switchToPersonal: async () => {
        const currentPath = window.location.pathname;
        const currentSpaceId = currentPath.match(/\/spaces\/([^\/]+)/)?.[1];

        // Resolve the user's personal org id from the backend before switching.
        let personalOrgId: string | null = null;
        let personalOrgName: string | null = null;
        try {
          const ctx = await fetchService(`${API_BASE_URL}/user/context`, { method: 'GET' });
          // The server resolves 'personal' alias to the actual personal org.
          personalOrgId = ctx.org_id || null;
          personalOrgName = ctx.org_name || null;
        } catch (err) {
          log.warn('[switchToPersonal] Could not resolve personal org from backend:', err);
        }

        // Check if already on the personal org (no-op)
        const currentContext = get().currentContext;
        if (personalOrgId && currentContext.orgId === personalOrgId) {
          log.info(`[switchToPersonal] Already on personal org ${personalOrgId.slice(-8)}, no-op`);
          return;
        }

        await get().setContext({
          type: 'org',
          orgId: personalOrgId,
          orgName: personalOrgName,
        });
        
        // If currently on a space, it MUST be an org space (since we just switched FROM org)
        // Therefore we need to navigate to a personal space
        if (currentSpaceId) {
          log.info(`[switchToPersonal] Currently on space ${currentSpaceId} (must be org space), finding personal space`);
        }
        
        // Find a personal space to navigate to
        try {
          // Fetch all spaces to find personal ones
          const allSpaces = await fetchService(`${API_BASE_URL}/entities?entityType=Space`, {
            method: 'GET'
          });
          
          // The backend already scopes the response to the current dataspace context
          // (personal org), so all returned spaces belong to the personal org.
          // Do NOT filter by !s.org_id — personal org spaces have org_id set.
          const personalSpaces = allSpaces;
          log.info(`[switchToPersonal] Found ${personalSpaces.length} personal spaces`);
          
          if (personalSpaces.length === 0) {
            // No personal spaces - create one
            log.info(`[switchToPersonal] No personal spaces found, creating one`);
            const newSpace = await fetchService(`${API_BASE_URL}/spaces`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: 'New Space',
                metadata: { is_default: true }
              })
            });
            
            if (newSpace && newSpace._id) {
              log.info(`[switchToPersonal] Created personal space: ${newSpace._id}`);
              get().setPendingNavigation({ type: 'space', spaceId: newSpace._id });
              get().setLastVisitedSpace(newSpace._id);
            }
            return;
          }
          
          // Try last visited personal space first (keyed by org ID)
          const lastVisitedSpace = personalOrgId ? get().getLastVisitedSpace(personalOrgId) : null;
          if (lastVisitedSpace && personalSpaces.some((s: any) => s._id === lastVisitedSpace)) {
            log.info(`[switchToPersonal] Navigating to last visited personal space: ${lastVisitedSpace}`);
            get().setPendingNavigation({ type: 'space', spaceId: lastVisitedSpace });
            return;
          }
          
          // Navigate to most recent personal space
          const mostRecent = personalSpaces.sort((a: any, b: any) => 
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          )[0];
          
          if (mostRecent) {
            log.info(`[switchToPersonal] Navigating to most recent personal space: ${mostRecent._id}`);
            get().setPendingNavigation({ type: 'space', spaceId: mostRecent._id });
            get().setLastVisitedSpace(mostRecent._id);
          }
        } catch (error) {
          log.error(`[switchToPersonal] Error finding personal spaces:`, error);
          // Stay on current page if we can't find personal spaces
        }
      },
      
      // Switch to org dataspace
      switchToOrg: async (orgId: string) => {
        const org = get().userOrgs.find(o => o._id === orgId || o.id === orgId);
        
        if (!org) {
          throw new Error(`Organization ${orgId} not found`);
        }
        
        // ✅ Check if payment is required
        const billingStatus = org.skeleton.billing_status;
        if (billingStatus === 'payment_required' || billingStatus === 'past_due') {
          log.info(`[switchToOrg] ⚠️ Payment required for org ${orgId}, showing payment gate`);
          get().showPaymentGate(orgId);
          return; // Block switch
        }
        
        // Check if we're already in this org context (no-op)
        const currentContext = get().currentContext;
        if (currentContext.type === 'org' && currentContext.orgId === orgId) {
          log.info(`[switchToOrg] Already in org ${orgId} dataspace, no-op`);
          return;
        }
        
        // Check if currently on a space page
        const currentPath = window.location.pathname;
        const currentSpaceId = currentPath.match(/\/spaces\/([^\/]+)/)?.[1];
        
        await get().setContext({
          type: 'org',
          orgId: org._id || org.id,
          orgName: org.skeleton.displayName || org.name
        });

        // Refresh org list so per-user plan (my_plan), member counts, and
        // billing status reflect the latest server state after every switch.
        get().loadUserOrgs().catch(err =>
          log.warn('[switchToOrg] Non-fatal: failed to refresh orgs after switch', err)
        );

        // Find a Space to navigate to in the target org's dataspace.
        // The context switch (setContext above) already updated the backend and
        // invalidated the server-side user-entity cache, so GET /api/entities
        // is already scoped to the new org. We trust the backend response and do
        // NOT re-filter client-side by org_id — a client-side filter introduces a
        // race window where a stale per-worker cache hit returns old-context spaces,
        // causing the filter to yield zero results and a false "no spaces" error.
        try {
          const fetchService = (await import('../services/fetchService')).default;
          const { API_BASE_URL } = await import('../api/config');

          log.info(`[switchToOrg] 🔍 Fetching Spaces for org: ${orgId}`);
          let spaces: any[] = [];
          let apiFailed = false;
          try {
            const fetched = await fetchService(`${API_BASE_URL}/entities?entityType=Space`, {
              method: 'GET'
            });
            if (fetched && Array.isArray(fetched)) {
              spaces = fetched;
            }
            log.info(`[switchToOrg] ✅ Fetched ${spaces.length} Space(s) in new org context`);
          } catch (fetchError) {
            log.error('[switchToOrg] ❌ Backend error fetching Spaces', fetchError);
            apiFailed = true;
          }

          if (spaces.length === 0) {
            log.error('[switchToOrg] ⚠️ No Spaces found for org — backend should have created one at org creation time');
            throw new Error(apiFailed
              ? 'Failed to fetch Spaces from backend. Please refresh the page and try again.'
              : 'No spaces found for this org. Please refresh the page.');
          } else {
            // Spaces exist — navigate to last visited or most recent
            const lastVisitedSpace = get().getLastVisitedSpace(orgId);
            if (lastVisitedSpace && spaces.some((s: any) => s._id === lastVisitedSpace)) {
              log.info(`[switchToOrg] Navigating to last visited Space: ${lastVisitedSpace}`);
              get().setPendingNavigation({ type: 'space', spaceId: lastVisitedSpace });
            } else {
              const mostRecent = spaces.sort((a: any, b: any) =>
                new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
              )[0];
              if (mostRecent) {
                log.info(`[switchToOrg] Navigating to most recent Space: ${mostRecent._id}`);
                get().setPendingNavigation({ type: 'space', spaceId: mostRecent._id });
                get().setLastVisitedSpace(mostRecent._id);
              }
            }
          }
        } catch (error) {
          log.error('[switchToOrg] Error finding Spaces in new org:', error);
          // Context already switched — navigate to /home so the user isn't
          // stranded on a page from the previous org.
          get().setPendingNavigation({ type: 'home' });
        }
      },
      
      // Refresh context from backend.
      // If the server-side context differs from the current store value (e.g. another device
      // switched teams), performs a full store sync via applyRemoteContextChange so every
      // slice — entityStore, systemStore, billing — reflects the authoritative org.
      refreshContext: async () => {
        set({ isLoading: true, error: null });

        try {
          log.info('[refreshContext] Refreshing context from backend');

          const data = await fetchService(`${API_BASE_URL}/user/context`, {
            method: 'GET',
          });

          const freshOrgId = data.org_id || null;
          const freshOrgName = data.org_name || null;
          const { currentContext } = get();

          if (freshOrgId && freshOrgId !== currentContext.orgId && currentContext.orgId !== null) {
            // Context was changed on another device while this one was active/backgrounded.
            // Delegate to applyRemoteContextChange for the full sync.
            // Note: the backend always returns a non-null org_id (personal contexts are
            // healed to the personal org) — so freshOrgId is reliably truthy here, and
            // the currentContext.orgId !== null guard skips sync on first-load only.
            log.info('[refreshContext] Remote context change detected, syncing stores');
            await get().applyRemoteContextChange(freshOrgId, freshOrgName);
          } else {
            // First load or same context — light update, keep stores consistent.
            const newContext = { type: 'org' as const, orgId: freshOrgId, orgName: freshOrgName };
            set({ currentContext: newContext });
            try {
              const { useSystemStore } = await import('./systemStore');
              useSystemStore.setState({ currentContext: newContext });
            } catch { /* non-fatal */ }
            log.info('[refreshContext] Context refreshed (no change):', data);
          }
        } catch (error: any) {
          log.error('[refreshContext] Error:', error);
          set({ error: error.message || 'Failed to refresh context' });
        } finally {
          set({ isLoading: false });
        }
      },

      // Apply a context change that originated on another device.
      // Performs the same store-sync work as setContext but skips the backend POST and
      // the pre-switch flush (we can't block the user without warning for a passive sync).
      applyRemoteContextChange: async (orgId: string, orgName: string | null) => {
        const { currentContext } = get();
        if (currentContext.orgId === orgId) return; // already in sync

        log.info('[applyRemoteContextChange] Applying remote context change', { orgId, orgName });

        const newContext: DataspaceContext = { type: 'org', orgId, orgName };

        // Update this store
        set({ currentContext: newContext });

        // Mirror into systemStore + clear the spaces list cache
        try {
          const { useSystemStore } = await import('./systemStore');
          useSystemStore.setState({ currentContext: newContext, recentSpaces: [], spacesCacheTime: null });
        } catch { /* non-fatal */ }

        // Switch the entity store to the new dataspace
        try {
          const { default: useEntityStore } = await import('./entityStore');
          const entityStore = useEntityStore.getState();
          const dataspaceKey = orgId;

          // Persist current entities for the outgoing dataspace before evicting
          if (entityStore.currentDataspaceKey && entityStore.entities.length > 0) {
            const prevKey = entityStore.currentDataspaceKey;
            useEntityStore.setState(s => ({
              dataspaceEntities: { ...s.dataspaceEntities, [prevKey]: [...s.entities] },
            }));
            log.info(`[applyRemoteContextChange] Saved ${entityStore.entities.length} entities for ${prevKey}`);
          }

          // Switch key so subsequent reads land in the right bucket
          useEntityStore.setState({ currentDataspaceKey: dataspaceKey });

          // Clear IndexedDB cache for the incoming dataspace (force fresh fetch)
          try {
            const { entityCacheService } = await import('../services/entityCacheService');
            const { getCurrentUserId } = await import('../utils/userIdUtils');
            const { getDataspaceCacheKey } = await import('../utils/entityCacheUtils');
            const userId = getCurrentUserId();
            if (userId) {
              const cacheKey = getDataspaceCacheKey(userId, 'org', orgId);
              await entityCacheService.delete(cacheKey);
              log.info(`[applyRemoteContextChange] Cleared stale cache for dataspace: ${cacheKey.substring(0, 40)}...`);
            }
          } catch { /* non-fatal */ }

          // Evict in-memory entity cache for the incoming dataspace
          useEntityStore.setState(s => {
            const next = { ...s.dataspaceEntities };
            delete next[dataspaceKey];
            return { dataspaceEntities: next };
          });

          // Reset entity store so initialize() triggers a fresh fetch
          useEntityStore.setState({
            entities: [],
            initialized: false,
            status: 'idle',
            lifecycle: 'uninitialized',
            initializationPromise: null,
          });
          log.info(`[applyRemoteContextChange] Triggering entity reload for dataspace: ${dataspaceKey}`);

          const dataspaceRoomKey = `org:${orgId}`;
          try {
            await useEntityStore.getState().initialize(dataspaceRoomKey);
            log.info(`[applyRemoteContextChange] EntityStore re-initialized for ${dataspaceKey}`);
          } catch (e) {
            log.error('[applyRemoteContextChange] Failed to re-initialize entityStore:', e);
          }
        } catch (error) {
          log.error('[applyRemoteContextChange] Failed to switch entityStore dataspace:', error);
        }

        // Re-fetch user profile and billing so plan/usage reflects the new org
        try {
          const userStore = (await import('./userStore')).default;
          userStore.getState().fetchProfile().catch((e: any) =>
            log.warn('[applyRemoteContextChange] Non-fatal: fetchProfile failed', e)
          );
          userStore.getState().fetchCurrentBill({ force: true }).catch((e: any) =>
            log.warn('[applyRemoteContextChange] Non-fatal: fetchCurrentBill failed', e)
          );
        } catch { /* non-fatal */ }

        log.info('[applyRemoteContextChange] Context updated to org:', orgId);
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },
      
      // ✅ FIX: Set pending navigation (replaces sessionStorage)
      setPendingNavigation: (nav: PendingNavigation | null) => {
        set({ pendingNavigation: nav });
      },
      
      // Set last visited space for current dataspace (always keyed by orgId)
      setLastVisitedSpace: (spaceId: string) => {
        const { currentContext, lastVisitedSpaces } = get();
        const contextKey = currentContext.orgId || '';
        
        if (contextKey) {
          set({ 
            lastVisitedSpaces: {
              ...lastVisitedSpaces,
              [contextKey]: spaceId
            }
          });
          log.info(`[setLastVisitedSpace] Set last visited space for ${contextKey}: ${spaceId}`);
        }
      },
      
      // Get last visited space for a dataspace context (always keyed by orgId)
      getLastVisitedSpace: (contextKey?: string) => {
        const { currentContext, lastVisitedSpaces } = get();
        const key = contextKey || currentContext.orgId || '';
        
        return key ? lastVisitedSpaces[key] || null : null;
      },
      
      // Show payment gate for unpaid org
      showPaymentGate: (orgId: string) => {
        const org = get().userOrgs.find(o => o._id === orgId || o.id === orgId);
        
        if (org) {
          set({
            paymentGate: {
              isOpen: true,
              orgId: org._id || org.id,
              orgName: org.skeleton.displayName || org.name,
              seatCount: org.skeleton.seat_count || 1
            }
          });
        }
      },
      
      // Hide payment gate
      hidePaymentGate: () => {
        set({
          paymentGate: {
            isOpen: false,
            orgId: null,
            orgName: null,
            seatCount: 1,
            planTier: undefined
          }
        });
      },
      
      // ✅ FIX #6: Update payment gate plan tier
      updatePaymentGatePlan: (planTier: string) => {
        const current = get().paymentGate;
        set({
          paymentGate: {
            ...current,
            planTier
          }
        });
      }
    }),
    {
      name: 'dataspace-context',
      // Only persist lastVisitedSpaces — NOT currentContext.
      // currentContext is re-synced from the backend on every App load via GET /user/context.
      partialize: (state) => ({
        lastVisitedSpaces: state.lastVisitedSpaces,
        lastOrgLoadAt: state.lastOrgLoadAt,
      }),
    }
  )
);

// Helper functions for use outside the store

/**
 * Get the current dataspace context
 */
export const getCurrentDataspaceContext = (): DataspaceContext => {
  return useDataspaceContextStore.getState().currentContext;
};

/**
 * Check if currently in an org dataspace
 */
export const isInOrgDataspace = (): boolean => {
  return getCurrentDataspaceContext().type === 'org';
};

/**
 * Get current org ID (if in org dataspace)
 */
export const getCurrentOrgId = (): string | null => {
  return getCurrentDataspaceContext().orgId;
};

/**
 * React hook — returns the current org's Root Space ID.
 * Null while it is being resolved or there is no org context.
 */
export const useRootSpaceId = (): string | null =>
  useDataspaceContextStore(s => s.rootSpaceId);

/**
 * Initialize dataspace context on app start
 */
export const initializeDataspaceContext = async () => {
  const store = useDataspaceContextStore.getState();
  
  try {
    // Load user's orgs
    await store.loadUserOrgs();
    
    // Refresh context from backend (in case it changed on another device)
    await store.refreshContext();
    
    log.info('[initializeDataspaceContext] Dataspace context initialized');
  } catch (error) {
    log.error('[initializeDataspaceContext] Failed to initialize:', error);
  }
};


